import { useState, useRef, useCallback, useEffect } from 'react'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || ''

const PinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onSelect: (address: string, city: string, state: string, zip: string) => void
  placeholder?: string
  className?: string
}

interface MapboxFeature {
  place_name: string
  address?: string
  text: string
  context?: { id: string; text: string; short_code?: string }[]
}

function parseFeature(feature: MapboxFeature) {
  const street = feature.address ? `${feature.address} ${feature.text}` : feature.text
  let city = ''
  let state = ''
  let zip = ''

  if (feature.context) {
    for (const ctx of feature.context) {
      if (ctx.id.startsWith('postcode')) zip = ctx.text
      else if (ctx.id.startsWith('place')) city = ctx.text
      else if (ctx.id.startsWith('region')) state = ctx.short_code?.replace('US-', '') || ctx.text
    }
  }

  return { street, city, state, zip }
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing an address...',
  className = 'rfp-input',
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<MapboxFeature[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const wrapRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!showSuggestions) return
    const handle = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [showSuggestions])

  const fetchSuggestions = useCallback((query: string) => {
    clearTimeout(debounceRef.current)
    if (!MAPBOX_TOKEN || query.trim().length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const encoded = encodeURIComponent(query.trim())
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&types=address&country=US`
        )
        const data = await res.json()
        if (data.features?.length) {
          setSuggestions(data.features)
          setShowSuggestions(true)
        } else {
          setSuggestions([])
          setShowSuggestions(false)
        }
      } catch {
        /* silent */
      }
    }, 300)
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    onChange(val)
    fetchSuggestions(val)
  }

  const handleSelect = (feature: MapboxFeature) => {
    const parsed = parseFeature(feature)
    onChange(parsed.street)
    onSelect(parsed.street, parsed.city, parsed.state, parsed.zip)
    setSuggestions([])
    setShowSuggestions(false)
  }

  return (
    <div className="addr-wrap" ref={wrapRef}>
      <input
        type="text"
        className={className}
        value={value}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true) }}
        placeholder={placeholder}
        autoComplete="off"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="addr-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={i}
              className="addr-suggestion"
              onClick={() => handleSelect(s)}
              type="button"
            >
              <PinIcon />
              <span>{s.place_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
